import React from 'react'

import { Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Group } from '@/core/api/entities/group'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { usePermission } from '@/core/hooks/usePermission'
import { AddButton } from '@/features/zones/components/AddButton'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
} from '@/shared/components/molecules/Carousel'
import { DraftBadge } from '@/shared/components/molecules/DraftBadge/DraftBadge'
import PlaylistCard from '@/shared/components/molecules/PlaylistCard/PlaylistCard'
import { SaveDiscardButtons } from '@/shared/components/molecules/SaveDiscardButtons/SaveDiscardButtons'
import PageLayout from '@/shared/components/organisms/Layout/PageLayout'

import { AdCampaignsSection } from '../zone/components/AdCampaignsSection'
import { AssignedGroups } from '../zone/components/AssignedGroupsSection'
import { DeleteSection } from '../zone/components/DeleteSection'
import { SchedulesSection } from '../zone/components/SchedulesSection'

interface GroupDetailsUIProps {
	group?: Group
	isLoading: boolean
	canEditGroup?: boolean
	canDeleteGroup?: boolean
	onEditGroup: () => void
	onRemoveZone: (zoneId: number) => void
	onAddZone: () => void
	onRemoveCampaign: (campaignId: number) => void
	onAddSchedule: () => void
	onRemoveSchedule: (scheduleId: number) => void
	onAddCampaign: () => void
	onAddPlaylist: () => void
	onRemovePlaylist: (playlistId: number) => void
	onDeleteGroup: () => void
	hasUnsavedChanges?: boolean
	isSaving?: boolean
	onSaveChanges?: () => void
	onDiscardChanges?: () => void
}

const GroupDetailsUI: React.FC<GroupDetailsUIProps> = ({
	group,
	isLoading,
	canEditGroup = false,
	canDeleteGroup = false,
	onEditGroup,
	onRemoveZone,
	onAddZone,
	onRemoveCampaign,
	onAddSchedule,
	onRemoveSchedule,
	onAddCampaign: onAddCampaignNew,
	onAddPlaylist,
	onRemovePlaylist,
	onDeleteGroup,
	hasUnsavedChanges = false,
	isSaving = false,
	onSaveChanges,
	onDiscardChanges,
}) => {
	const { t } = useTranslation()
	const { features } = usePermission()

	const headerActions =
		hasUnsavedChanges && onSaveChanges && onDiscardChanges ? (
			<div className="hidden lg:flex">
				<SaveDiscardButtons
					isSaving={isSaving}
					onSave={onSaveChanges}
					onDiscard={onDiscardChanges}
				/>
			</div>
		) : undefined
	if (isLoading) {
		return (
			<PageLayout
				backButton={{ show: true, to: '/zones?tab=group' }}
				title={t('entities.group.details.title')}
				contentClassName="min-h-[calc(100vh-300px)]"
				headerActions={headerActions}
			>
				<div className="space-y-8">
					<Skeleton className="h-6 w-64" />
					<div className="space-y-4">
						<Skeleton className="h-5 w-48" />
						<Skeleton className="h-10 w-full" />
					</div>
				</div>
			</PageLayout>
		)
	}

	if (!group) {
		return (
			<PageLayout
				backButton={{ show: true, to: '/zones?tab=group' }}
				title={t('entities.group.details.title')}
				headerActions={headerActions}
			>
				<p className="text-muted-foreground">
					{t('entities.group.details.not_found')}
				</p>
			</PageLayout>
		)
	}

	const zones = group.zones || []
	const playlists = group.playlists || []
	const schedules = group.schedules || []
	const campaigns = group.campaigns || []

	return (
		<PageLayout
			backButton={{ show: true, to: '/zones?tab=group' }}
			title={t('entities.group.details.title')}
			contentClassName="px-8 py-6 min-h-[calc(100vh-300px)]"
			headerActions={headerActions}
		>
			<div className="space-y-8">
				{/* Mobile Save/Discard Buttons - shown above group name on mobile */}
				{hasUnsavedChanges && onSaveChanges && onDiscardChanges && (
					<div className="lg:hidden">
						<SaveDiscardButtons
							isSaving={isSaving}
							onSave={onSaveChanges}
							onDiscard={onDiscardChanges}
						/>
					</div>
				)}

				{/* Group Name Section */}
				<div className="flex items-center gap-3">
					<h2 className="text-2xl font-medium">{group.name}</h2>
					<DraftBadge visible={hasUnsavedChanges} />
					{canEditGroup && (
						<button
							onClick={onEditGroup}
							className="text-muted-foreground hover:text-foreground transition-colors p-1"
						>
							<Pencil className="h-4 w-4" />
						</button>
					)}
				</div>

				<AssignedGroups
					title={t('entities.group.details.assigned_zones_title')}
					items={zones}
					onAdd={features.zone.create ? onAddZone : undefined}
					onRemove={onRemoveZone}
					addButtonText={t('entities.group.details.add_zone')}
					emptyMessage={t('entities.group.details.no_zones_assigned')}
				/>

				{/* Playlists Section */}
				<div className="space-y-4 mb-12">
					<div className="flex items-center gap-3">
						<h2 className="text-xl font-semibold">
							{t('zones:details.playlists.title')}
						</h2>
						<AddButton onAdd={onAddPlaylist} />
					</div>
					{playlists.length === 0 ? (
						<div className="text-sm text-muted-foreground">
							{t('zones:details.playlists.noPlaylists')}
						</div>
					) : (
						<Carousel
							opts={{
								align: 'start',
								slidesToScroll: 1,
							}}
							className="w-full"
						>
							<CarouselContent className="-ml-4 flex flex-wrap">
								{playlists.map((playlist: Playlist) => (
									<CarouselItem
										key={playlist.id}
										className="pl-4 basis-auto pb-4"
									>
										<PlaylistCard
											playlist={playlist}
											showFavoriteIcon={false}
											onDelete={() => onRemovePlaylist(playlist.id)}
										/>
									</CarouselItem>
								))}
							</CarouselContent>
						</Carousel>
					)}
				</div>

				<SchedulesSection
					schedules={schedules}
					onAdd={onAddSchedule}
					onRemove={onRemoveSchedule}
				/>

				<AdCampaignsSection
					campaigns={campaigns}
					groups={[]}
					variant="group"
					onAdd={onAddCampaignNew}
					onRemove={onRemoveCampaign}
				/>

				{canDeleteGroup && (
					<DeleteSection
						deleteText={t('entities.group.actions.delete_group')}
						onDelete={onDeleteGroup}
					/>
				)}
			</div>
		</PageLayout>
	)
}

export default GroupDetailsUI
